import { useEffect, useMemo, useState, useCallback } from 'react'
import MapView from '../components/MapView/index.js'
import Navigation from '../components/Navigation.jsx'
import Footer from '../components/Footer.jsx'
import SearchForm from '../components/SearchForm.jsx'
import ResultsGrid from '../components/ResultsGrid.jsx'
import TravelingPlanSidebar from '../components/TravelingPlanSidebar.jsx'
import MobilePlanDrawer from '../components/MobilePlanDrawer.jsx'
import FloatingActionButton from '../components/FloatingActionButton.jsx'
import { PlanSelectionPopup } from '../components/PlanSelectionPopup.jsx'
import { CreatePlanModal } from '../components/CreatePlanModal.jsx'
import DebugPanel from '../components/DebugPanel.jsx'
import LandingPage from '../components/LandingPage.jsx'
import { OrganizationSchema } from '../components/SchemaOrg.jsx'
import { formatDateForInput, formatSelectedDateLabel } from '../lib/searchUtils.js'
import { getMapApiKey } from '../lib/mapConfig.js'
import { useMapPosition } from '../hooks/useMapPosition.js'
import { useSearch } from '../hooks/useSearch.js'
import { useCategoryFilter } from '../hooks/useCategoryFilter.js'
import { useSavedItems } from '../hooks/useSavedItems.js'
import { useTravelingPlan } from '../hooks/useTravelingPlan.js'

const today = new Date()
const ENABLE_SEARCH = import.meta.env.VITE_ENABLE_SEARCH !== 'false'

export default function HomePage() {
    // If search is disabled, show landing page
    if (!ENABLE_SEARCH) {
        return <LandingPage />
    }
    const [selectedDate, setSelectedDate] = useState(() => formatDateForInput(today))
    const [hasSearched, setHasSearched] = useState(false)
    const [pendingSearch, setPendingSearch] = useState(false)
    const [results, setResults] = useState([])
    const [selectedId, setSelectedId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [isPlanCollapsed, setIsPlanCollapsed] = useState(true)
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)
    const [planPopup, setPlanPopup] = useState(null) // { item, anchorEl }
    const [showCreatePlanModal, setShowCreatePlanModal] = useState(false)
    const [editingPlan, setEditingPlan] = useState(null)

    const { mapCenter, mapZoom, mapReady, mapBounds, handleMapReady, handleMapIdle } = useMapPosition()

    const { savedItems, toggleSaveItem, isSaved } = useSavedItems()

    const {
        plans,
        planItems,
        planByDate,
        togglePlanItem,
        getItemPlans,
        removeFromPlan,
        clearPlan,
        createPlan,
        updatePlan,
        deletePlan,
        getPlan
    } = useTravelingPlan()

    // Handler to show plan selection popup
    const handleShowPlanPopup = useCallback((item, anchorEl) => {
        setPlanPopup({ item, anchorEl })
    }, [])

    // Handler to close plan selection popup
    const handleClosePlanPopup = useCallback(() => {
        setPlanPopup(null)
    }, [])

    // Handler to toggle plan item (called from popup)
    const handleTogglePlanItem = useCallback((item, date, planId) => {
        togglePlanItem(item, date, planId)
    }, [togglePlanItem])

    // Handler to remove item from plan
    const handleRemoveFromPlan = useCallback((itemId, planId, date) => {
        removeFromPlan(itemId, planId, date)
    }, [removeFromPlan])

    // Handlers for create/edit plan modals
    const handleShowCreateModal = useCallback(() => {
        setShowCreatePlanModal(true)
    }, [])

    const handleShowEditModal = useCallback((plan) => {
        setEditingPlan(plan)
    }, [])

    const handleCreatePlan = useCallback((name, color) => {
        const newPlan = createPlan(name, color)
        setShowCreatePlanModal(false)
        return newPlan
    }, [createPlan])

    const handleUpdatePlan = useCallback((name, color) => {
        updatePlan(editingPlan.id, { name, color })
        setEditingPlan(null)
    }, [updatePlan, editingPlan])

    // Handler to close mobile drawer
    const handleCloseMobileDrawer = useCallback(() => {
        setIsMobileDrawerOpen(false)
    }, [])

    // Handler to open mobile drawer
    const handleOpenMobileDrawer = useCallback(() => {
        setIsMobileDrawerOpen(true)
    }, [])

    const { handleSearch, handleSearchRef, loadMore, hasMore, loadingMore } = useSearch({
        mapReady,
        mapCenter,
        mapZoom,
        mapBounds,
        selectedDate,
        setResults,
        setSelectedId,
        setLoading,
        setError,
    })

    const {
        categoryOptions,
        activeCategoryKeys,
        filteredResults,
        toggleCategory,
        clearCategories,
    } = useCategoryFilter(results)

    const sortedResults = useMemo(() => {
        return [...filteredResults].sort((a, b) => {
            const aTime = Number(a?.time_start ?? Number.POSITIVE_INFINITY)
            const bTime = Number(b?.time_start ?? Number.POSITIVE_INFINITY)
            return aTime - bTime
        })
    }, [filteredResults])

    const selectedDateLabel = useMemo(
        () => formatSelectedDateLabel(selectedDate),
        [selectedDate],
    )

    const selectedItem = useMemo(
        () => filteredResults.find(item => item.id === selectedId) ?? null,
        [filteredResults, selectedId],
    )

    useEffect(() => {
        if (!selectedId) {
            return
        }
        const stillVisible = filteredResults.some(item => item.id === selectedId)
        if (!stillVisible) {
            setSelectedId(null)
        }
    }, [filteredResults, selectedId])

    useEffect(() => {
        if (!selectedId || typeof document === 'undefined') {
            return
        }
        const escapedId =
            typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
                ? CSS.escape(selectedId)
                : selectedId
        const element = document.querySelector(`[data-result-id="${escapedId}"]`)
        if (element && element.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [selectedId])

    useEffect(() => {
        if (!pendingSearch || !mapReady) {
            return
        }
        handleSearchRef.current?.()
        setPendingSearch(false)
    }, [mapReady, pendingSearch])

    const mapApiKey = getMapApiKey()

    const mainClasses = hasSearched
        ? 'flex flex-1 flex-col gap-8 px-4 py-6 sm:px-6 lg:px-16'
        : 'flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-16'

    // Calculate total item count across all plans and likes for FAB badge
    const totalItemCount = useMemo(() => {
        return planItems.length + savedItems.length
    }, [planItems, savedItems])

    const handleFormSubmit = event => {
        event.preventDefault()
        if (!selectedDate) {
            setError('Please choose a date.')
            return
        }
        setHasSearched(true)

        if (mapReady) {
            handleSearchRef.current?.()
        } else {
            setPendingSearch(true)
        }
    }

    return (
        <>
            <OrganizationSchema />
            <div className="flex min-h-screen flex-col bg-slate-50">
                <Navigation />

            <div className="flex flex-1">
                {/* Left sidebar for plans and likes */}
                <aside className="hidden lg:block sticky top-0 h-screen overflow-visible">
                    <TravelingPlanSidebar
                        plans={plans}
                        planByDate={planByDate}
                        onRemoveItem={handleRemoveFromPlan}
                        onClearPlan={clearPlan}
                        onCreatePlan={createPlan}
                        onUpdatePlan={updatePlan}
                        onDeletePlan={deletePlan}
                        onShowCreateModal={handleShowCreateModal}
                        onShowEditModal={handleShowEditModal}
                        savedItems={savedItems}
                        onRemoveSaved={toggleSaveItem}
                        isCollapsed={isPlanCollapsed}
                        onToggleCollapse={() => setIsPlanCollapsed(!isPlanCollapsed)}
                    />
                </aside>

                <main className={mainClasses}>
                <SearchForm
                    selectedDate={selectedDate}
                    onDateChange={event => setSelectedDate(event.target.value)}
                    onSubmit={handleFormSubmit}
                    loading={loading}
                    error={error}
                    hasSearched={hasSearched}
                    selectedDateLabel={selectedDateLabel}
                />

                {hasSearched && (
                    <section className="flex flex-col gap-6">
                        <MapView
                            apiKey={mapApiKey}
                            results={filteredResults}
                            selectedId={selectedId}
                            selectedItem={selectedItem}
                            onSelect={setSelectedId}
                            onIdle={handleMapIdle}
                            onReady={handleMapReady}
                            isLoading={loading}
                            categoryOptions={categoryOptions}
                            activeCategoryKeys={activeCategoryKeys}
                            onToggleCategory={toggleCategory}
                            onClearCategories={clearCategories}
                        />

                        <div className="space-y-4">
                            <ResultsGrid
                                results={sortedResults}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                                selectedDate={selectedDate}
                                loading={loading}
                                error={error}
                                isSaved={isSaved}
                                onToggleSave={toggleSaveItem}
                                plans={plans}
                                getItemPlans={getItemPlans}
                                onShowPlanPopup={handleShowPlanPopup}
                                hasMore={hasMore}
                                loadingMore={loadingMore}
                                onLoadMore={loadMore}
                            />
                        </div>
                    </section>
                )}
            </main>
            </div>

            {/* Plan selection popup */}
            {planPopup && (
                <PlanSelectionPopup
                    item={planPopup.item}
                    selectedDate={selectedDate}
                    plans={plans}
                    getItemPlans={getItemPlans}
                    onTogglePlan={handleTogglePlanItem}
                    onCreatePlan={handleCreatePlan}
                    onClose={handleClosePlanPopup}
                    anchorEl={planPopup.anchorEl}
                />
            )}

            {/* Create/Edit plan modals - rendered at top level */}
            {showCreatePlanModal && (
                <CreatePlanModal
                    onClose={() => setShowCreatePlanModal(false)}
                    onCreate={handleCreatePlan}
                />
            )}

            {editingPlan && (
                <CreatePlanModal
                    onClose={() => setEditingPlan(null)}
                    onCreate={handleUpdatePlan}
                    existingPlan={editingPlan}
                />
            )}

            <Footer />

            {/* Mobile plan drawer - only visible on mobile/tablet */}
            <MobilePlanDrawer
                isOpen={isMobileDrawerOpen}
                onClose={handleCloseMobileDrawer}
                plans={plans}
                planByDate={planByDate}
                onRemoveItem={handleRemoveFromPlan}
                onClearPlan={clearPlan}
                onCreatePlan={createPlan}
                onUpdatePlan={updatePlan}
                onDeletePlan={deletePlan}
                onShowCreateModal={handleShowCreateModal}
                onShowEditModal={handleShowEditModal}
                savedItems={savedItems}
                onRemoveSaved={toggleSaveItem}
            />

            {/* Floating action button - only visible on mobile/tablet */}
            <FloatingActionButton
                onClick={handleOpenMobileDrawer}
                itemCount={totalItemCount}
            />

            {/* Debug panel for viewing errors on mobile */}
            <DebugPanel />
            </div>
        </>
    )
}
